// Import React Libraries
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

// Import Ionic
import {
  useIonViewWillEnter,
  IonToolbar,
  IonInput,
  IonIcon,
  IonRow,
  IonInfiniteScrollContent,
  IonInfiniteScroll,
  IonGrid,
  IonButtons,
  IonLoading,
  useIonToast,
  useIonViewDidEnter,
  IonSpinner,
} from "@ionic/react";

// Import Styles
import classNames from "classnames/bind";
import styles from "./ProductsSearchPageLayout.module.scss";

// Import Components
import { SendUserRequestFormFab } from "../SendUserRequestFormFab/SendUserRequestFormFab";
import MaxWidthContainer from "../UI/MaxWidthContainer/MaxWidthContainer";
import PageTitle from "../PageTitle/PageTitle";
import { useHistory, useLocation } from "react-router";
import { AppContext } from "../../providers/Redux/Reducers/AppContext";
import { searchIcon } from "@core/icons";
import { closeOutline, search } from "ionicons/icons";
import BackButton from "../BackButton/BackButton";
import useSearchProductsInfiniteQuery from "../../providers/products/useSearchProductsInfiniteQuery";
import { useConfig } from "../../providers/config";
import Page from "../../pages/page-layout";
import { NewListViewProduct } from "@core/components";
import { getAppConfig } from "@core/config";
import { Product } from "@core/types";
import _ from "lodash";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import { useTranslation } from "react-i18next";

interface IProductsSearchPageLayout {
  pageTitle: string;
  breadcrumbsTitle: string;
}

interface ILocationState {
  isSearchInputFocused: boolean;
}

const cx = classNames.bind(styles);

const ProductsSearchPageLayoutOld: React.FC<IProductsSearchPageLayout> = ({
  children,
  pageTitle,
  breadcrumbsTitle,
}) => {
  const [presentToast] = useIonToast();
  const { t } = useTranslation();

  const history = useHistory();
  const [searchText, setSearchText] = useState("");

  const _searchTextRef = useRef("");
  const { activeShippingZone } = useActiveOrMainShippingZone();
  const [chosenCategory, setChosenCategory] = useState<string | null>(null);

  const {
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetched,
    isFetchingNextPage,
    data: searchResult,
  } = useSearchProductsInfiniteQuery({
    params: {
      keyword: searchText,
      zoneId: activeShippingZone.id,
    },
    options: {
      enabled: searchText.length > 1,
    },
  });

  const infiniteScrollRef = useRef<HTMLIonInfiniteScrollElement>(null);
  const ionContentRef = useRef<HTMLIonContentElement>(null);
  const [hideBackButton, setHideBackButton] = useState(false);
  const location = useLocation<ILocationState>();

  const inputRef = useRef(null as any);
  const config = useConfig();

  const cleanSearch = useCallback(() => {
    setSearchText("");
    ionContentRef.current?.scrollToTop();
  }, [setSearchText]);

  useIonViewWillEnter(() => {
    if (location.pathname === "/search-main-categories") {
      setHideBackButton(true);
    }
  }, [location]);

  useIonViewWillEnter(() => {
    if (history.action !== "POP") {
      cleanSearch();
    }
  }, []);

  useIonViewDidEnter(() => {
    if (history.action === "PUSH" && location?.state?.isSearchInputFocused) {
      inputRef.current?.setFocus();
    }
  }, [location?.state?.isSearchInputFocused, history.action, inputRef.current]);

  useEffect(() => {
    if (isFetched && searchText.length > 1 && !searchResult?.pages[0].length) {
      presentToast({
        message: t("Nothing found"),
        duration: 2000,
      });
    }
  }, [isFetched, searchText, searchResult]);

  const groupedProducts = useMemo(() => {
    return _.groupBy(
      searchResult?.pages.flatMap((p) => p),
      (p) => p.categories[0]?.name,
    );
  }, [searchResult]);

  return (
    <Page>
      <IonLoading isOpen={isFetching && !isFetched} message={t("Please wait...")} />
      <Page.Header mode="ios">
        <div className={cx("")}>
          <IonToolbar className={cx("toolbar", "ion-hide-md-up")}>
            <form
              onSubmit={(e) => {
                setChosenCategory(null);
                setSearchText(_searchTextRef.current);
                e.preventDefault();
              }}
              className={cx("searchBox__form")}
            >
              <div className={cx("searchBox", { searchBox__backButton: !hideBackButton })}>
                <IonButtons slot="start" className={cx("searchBox__buttons", { "ion-hide": hideBackButton })}>
                  <BackButton />
                </IonButtons>
                <IonInput
                  ref={inputRef}
                  // value={searchText}
                  type="search"
                  placeholder={t("Search in the store")}
                  onIonChange={(e) => {
                    if (!e.detail.value?.length) {
                      cleanSearch();
                    }
                    setChosenCategory(null);
                    setSearchText(e.detail.value || "");
                  }}
                  onInput={(e) => {
                    _searchTextRef.current = (e.target as HTMLInputElement).value;
                  }}
                  enterkeyhint="search"
                  inputMode="search"
                  debounce={getAppConfig().search.inputDebounceTimeMs}
                  className={cx("searchBox__input", "ion-no-padding")}
                />
                {!!searchText.length && (
                  <div onClick={cleanSearch}>
                    <div className={cx("cleanSearch", "cleanSearch--sm", "ion-hide-md-up")}>
                      <IonIcon className={cx("cleanSearch__icon", "cleanSearch__icon--sm")} icon={closeOutline} />
                    </div>
                    <div className={cx("cleanSearch", "cleanSearch--md", "ion-hide-md-down")}>
                      <IonIcon className={cx("cleanSearch__icon", "cleanSearch__icon--md")} icon={closeOutline} />
                    </div>
                  </div>
                )}
                <button type="submit" className={cx("submit-button")}>
                  <div className={cx("searchIcon", "searchIcon--sm", "ion-hide-md-up")}>
                    <IonIcon className={cx("searchIcon__icon", "searchIcon__icon--sm")} icon={searchIcon} />
                  </div>
                  <div className={cx("searchIcon", "searchIcon--md", "ion-hide-md-down")}>
                    <IonIcon className={cx("searchIcon__icon", "searchIcon__icon--md")} icon={search} />
                  </div>
                </button>
                {/*<button type="submit" className={cx("searchBar__button")}>*/}
                {/*    <IonIcon className={cx("search-icon")} icon={searchIcon} />*/}
                {/*</button>*/}
              </div>
            </form>
          </IonToolbar>
        </div>
      </Page.Header>
      <Page.Content ref={ionContentRef}>
        <Page.HeaderDesktop
          breadcrumbs={[
            {
              title: breadcrumbsTitle,
            },
          ]}
        />
        <div className={`${cx("productsContainer")} global-content`}>
          <MaxWidthContainer breakPoints={true}>
            <PageTitle hideTitleBreakpoint={"ion-hide-md-down"} title={pageTitle} />
            <div className={"ion-hide-md-up"}>
              {!!searchText.length && (searchResult?.pages[0].length || isFetching) ? (
                <MaxWidthContainer maxWidth={570}>
                  <IonGrid className={cx("ion-no-padding")}>
                    <IonRow className={cx("ml-2 mt-3")}>
                      {Object.entries(groupedProducts).map(([category, products]) => {
                        return (
                          <div
                            className={cx("block shadow-sm border-lightgray-800 border-1 p-[8px] m-1 rounded-xl", {
                              "border-secondary text-secondary": chosenCategory === category,
                            })}
                            key={category}
                            onClick={() => {
                              setChosenCategory((prev) => (prev === category ? null : category));
                            }}
                          >
                            {category}
                          </div>
                        );
                      })}
                    </IonRow>
                    <IonRow className={cx("productsRow")}>
                      {!chosenCategory ? (
                        isFetching && (!isFetchingNextPage || !isFetched) ? (
                          <div className={cx("spinner-wrap")}>
                            <IonSpinner className={cx("spinner")} name="crescent" />
                          </div>
                        ) : (
                          searchResult?.pages
                            ?.flatMap((el) => el)
                            .map((p: any) => <NewListViewProduct key={p.id} product={p} type="newList" />)
                        )
                      ) : (
                        groupedProducts[`${chosenCategory}`].map((p: Product) => (
                          <NewListViewProduct key={p.id} product={p} type="newList" />
                        ))
                      )}
                    </IonRow>
                    {/* Infinite Scroll  */}
                    <IonInfiniteScroll
                      threshold="100px"
                      ref={infiniteScrollRef}
                      disabled={!hasNextPage}
                      onIonInfinite={async () => {
                        if (hasNextPage) {
                          await fetchNextPage();
                        }
                        infiniteScrollRef.current?.complete();
                      }}
                    >
                      <IonInfiniteScrollContent
                        loadingText={t("Please Wait")}
                        loadingSpinner="crescent"
                      ></IonInfiniteScrollContent>
                    </IonInfiniteScroll>
                  </IonGrid>
                </MaxWidthContainer>
              ) : (
                children
              )}
            </div>
            <div className={"ion-hide-md-down"}>{children}</div>
          </MaxWidthContainer>
        </div>
        {config?.showSendCustomerRequestButton && <SendUserRequestFormFab />}
        <Page.ContentFooter />
      </Page.Content>
    </Page>
  );
};

export { ProductsSearchPageLayoutOld };
